import { cloneElement } from 'react';
import { Controller, FieldValues } from 'react-hook-form';
import { FormGroup, Label, FormFeedback } from 'reactstrap';
import classnames from 'classnames';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import SyncSelect, { MultiValue } from 'react-select';
import AsyncSelect from 'react-select/async';

import {
  UncontrolledTooltip,
  HelperText,
  WarningText,
} from '@shippypro/design-system-web';

import { getIconComponents } from './ControlledSelect.utils';
import { ControlledSelectProps } from './ControlledSelect.props';

export function ControlledSelect<FormType extends FieldValues, OptionType>({
  name,
  label,
  options,
  control,
  selectProps,
  asyncSelectProps,
  labelProps,
  helperTextProps,
  // here we can support one or more ways to show errors: FormFeedback, tooltip...
  errorModes = ['formFeedback'],
  errorTextProps,
  errorText,
  warningTextProps,
  warningText,
  successTextProps,
  successText,
  helperText,
  className,
  hasIcons,
  hasAvatars,
  hasImages,
  dropdownIndicator,
  indicatorSeparator,
  separator = true,
  isClearable = false,
  useExtractedValue,
  disabled,
  required,
  endLabelIcon,
  onChangeCustomAction,
  ...rest
}: ControlledSelectProps<FormType, OptionType>) {
  const tooltipTargetId = `${name.replace(/\./g, '-')}-tooltip-target`;

  // TODO: SHIP-1148
  // while refactoring controlled selects, look for a more elegant way for managing built in "components" object
  const iconComponents = getIconComponents({
    hasIcons,
    hasAvatars,
    hasImages,
    isMulti: selectProps?.isMulti,
  });

  const dropdownIndicatorComponent = dropdownIndicator
    ? { DropdownIndicator: dropdownIndicator }
    : {};

  const selectInput = !!asyncSelectProps ? (
    <AsyncSelect {...asyncSelectProps} />
  ) : (
    <SyncSelect />
  );

  return (
    <div id={tooltipTargetId}>
      <Controller
        control={control}
        render={({
          field: { onChange, onBlur, value },
          fieldState: { error },
        }) => (
          <FormGroup
            {...rest}
            className={classnames('!mb-0 w-full', className)}
          >
            <div className="flex space-x-1 label-icon-block">
              {label && (
                <Label {...labelProps}>
                  {label}
                  {required && <span className="ml-[2px]">*</span>}
                </Label>
              )}
              {endLabelIcon}
            </div>
            {errorModes.includes('tooltip') && (error || errorText) && (
              <UncontrolledTooltip target={tooltipTargetId} trigger="hover">
                {errorText ?? error?.message}
              </UncontrolledTooltip>
            )}
            {cloneElement(selectInput, {
              components: { ...iconComponents, ...dropdownIndicatorComponent },
              options,
              defaultValue: selectProps?.defaultValue,
              onChange: newValue => {
                if (selectProps?.getOptionValue && useExtractedValue) {
                  onChange(
                    selectProps?.isMulti
                      ? (newValue as MultiValue<OptionType>)?.map(v =>
                          selectProps.getOptionValue!(v),
                        )
                      : selectProps?.getOptionValue(newValue as OptionType),
                  );
                } else {
                  onChange(newValue);
                }
                onChangeCustomAction && onChangeCustomAction();
              },
              onBlur,
              classNamePrefix: 'select',
              className: classnames(
                `form-select-element ${selectProps?.className}`,
                {
                  'is-valid': Boolean(successText),
                  'is-invalid': Boolean(error || errorText),
                  'is-warning': warningText,
                },
              ),
              isDisabled: disabled || selectProps?.isDisabled,
              value:
                selectProps?.getOptionValue && useExtractedValue
                  ? selectProps?.isMulti
                    ? ((value as MultiValue<OptionType>)?.map(v =>
                        options?.find(option =>
                          isEqual(selectProps.getOptionValue!(option), v),
                        ),
                      ) as MultiValue<OptionType>)
                    : options?.find(
                        option => selectProps.getOptionValue!(option) === value,
                      )
                  : value,
              ...omit(selectProps, 'renderKey'),
              key: selectProps?.renderKey,
            })}
            {/* hidden input used for accessing value from Cypress */}
            <input
              type="hidden"
              data-test="select__value"
              value={
                value !== undefined &&
                value !== null &&
                !['number', 'string'].includes(typeof value) &&
                selectProps?.getOptionValue
                  ? selectProps?.getOptionValue(value)
                  : value ?? ''
              }
            />
            {(error || errorText) && errorModes.includes('formFeedback') && (
              <FormFeedback {...errorTextProps}>
                {errorText ?? error?.message}
              </FormFeedback>
            )}
            {successText && (
              <FormFeedback valid {...successTextProps}>
                {successText}
              </FormFeedback>
            )}
            {helperText && (
              <HelperText
                className="!text-[color:--shp-color-text-helper]"
                {...helperTextProps}
              >
                {helperText}
              </HelperText>
            )}
            {warningText &&
              warningText.map(text => (
                <WarningText {...warningTextProps}>{text}</WarningText>
              ))}
          </FormGroup>
        )}
        name={name}
      />
    </div>
  );
}

export type { ControlledSelectProps } from './ControlledSelect.props';
